import { normalise, pickLang, type ExerciseType, type Locale } from '@diana/shared'

/*
 * Small pure helpers for drawing an exercise item. Marking itself happens
 * on the server (answers never reach the browser before an attempt); these
 * only shape the prompt and read the solution that comes back.
 */

/** A gap in a prompt: „______”. */
export const GAP = /_{3,}/

/** Polish letters off is "almost", not wrong: dzien → dzień. */
function fold(s: string): string {
  return normalise(s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
}

function variants(answer: string): string[] {
  return answer.split(/\s+\/\s+|\s+·\s+/).map((v) => v.trim()).filter(Boolean)
}

/** True when the given answer matches an accepted one except for Polish letters. */
export function isAlmost(given: string, accepted: string[]): boolean {
  if (!given.trim()) return false
  const g = fold(given)
  return accepted.flatMap(variants).some((a) => fold(a) === g && normalise(a) !== normalise(given))
}

/** The accepted answer as the student should read it: the first spelling of each. */
export function shownAnswer(accepted: string[]): string {
  return accepted.map((a) => variants(a)[0] ?? a).join(' / ')
}

/**
 * The prompt without what the control already says: „P / F” at the end of a
 * true/false line, the „→ ___” a slot replaces, a/b/c options written into
 * the line (they are shown once, as tiles), and only the student's language
 * of a „🇺🇦 … · 🇷🇺 …” gloss.
 */
export function cleanPrompt(prompt: string, type: ExerciseType, locale: Locale, options: string[] = []): string {
  let p = prompt.trim()
  if (type === 'true_false') p = p.replace(/\s*\(?\s*P\s*\/\s*F\s*\)?\s*$/, '')
  if (type === 'match' || type === 'sort_groups') p = p.replace(/\s*(?:→|->)?\s*_{2,}\s*$/, '').replace(/\s*→\s*$/, '')
  if (type === 'choice' && options.length > 0) {
    const first = options[0]!.slice(0, 6)
    const at = p.search(/\s(?:a\)|a\.|\(a\))\s/)
    if (at > 0 && p.slice(at).indexOf(first) >= 0) p = p.slice(0, at)
  }
  if (p.indexOf('🇺🇦') >= 0 || p.indexOf('🇷🇺') >= 0) p = pickLang(p, locale)
  return p.trim()
}

/** „Jest rano. → ___” has a slot at the end: the arrow and the gap go, the control replaces them. */
export function stripTrailingGap(prompt: string): string {
  return prompt.replace(/\s*(?:→|->)\s*_{2,}\s*$/, '').replace(/\s*_{3,}\s*$/, '')
}

/** How many gaps a prompt has. Several gaps share one answer, joined with „; ” like the key writes it. */
export function gapCount(prompt: string): number {
  return prompt.split(GAP).length - 1
}

export const GAP_JOIN = '; '

export function splitGaps(value: string, n: number): string[] {
  const parts = value.split(GAP_JOIN)
  return Array.from({ length: n }, (_, i) => parts[i] ?? '')
}

/** „nazywa / Jak / pan / się / ?” → the tiles of an order-the-words item. */
export function wordTiles(prompt: string): string[] {
  const cleaned = prompt.replace(/\s*(?:→|->)\s*_{2,}\s*$/, '')
  const parts = cleaned.includes(' / ') ? cleaned.split(' / ') : cleaned.split(/\s+/)
  return parts.map((w) => w.trim()).filter(Boolean)
}

/** Tiles back into a sentence: no space before punctuation. */
export function joinTiles(tiles: string[]): string {
  return tiles.join(' ').replace(/\s+([?.!,;:…])/g, '$1')
}

export function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}

/** One gap of a multi-gap item, judged on its own once the key is known. */
export function gapState(given: string, accepted: string[], gap: number): 'correct' | 'almost' | 'wrong' {
  const parts = accepted.map((a) => a.split(GAP_JOIN.trim()).map((p) => p.trim())[gap] ?? '').filter(Boolean)
  const options = parts.flatMap(variants)
  if (options.some((o) => normalise(o) === normalise(given))) return 'correct'
  if (given.trim() && options.some((o) => fold(o) === fold(given))) return 'almost'
  return 'wrong'
}
